"use client";

import { memo, useContext, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useBoard, useEventRowIds, useRow } from "@/hooks/use-odds-live";
import { inplayRowId, ladderEventOf } from "@/lib/odds-screen/live/inplay";
import type { BookId, EventHeader, FamilyId, FamilySummary, LeagueId, OddsDisplay, RowId } from "@/lib/odds-screen/live/types";
import { LiveSubjectRow, useCellBet, type CellTarget, type HiddenRow } from "./board-rows";
import type { BetActions } from "./bet";
import { subjectCols } from "./subject";
import { PriceCell } from "./price-cell";
import { flashCells, lineSides, rowCells, type MarketCells } from "./row-model";
import { drawName, isTournament, matchupLabel, matchupSides, outcomeDisplayOrder, outcomeName, rowLines } from "./labels";
import { MatchupLogos } from "./matchup-logos";
import { KICKOFF_TIME, clock, metaDate, startedAt } from "./time";
import { LiveState } from "./live-state";
import { familyTitle, marketDisplayOrder, type FamilyCategory } from "./view-model";
import { StarButton } from "./star-button";
import { MinStakeContext } from "./use-min-stake";
import { HiddenCountButton } from "@/components/fantasy-props/chrome";

export const MAX_OPEN_MARKETS = 3;

export type GameDrill = { readonly rowId: RowId; readonly family: FamilyId; readonly slot: number; readonly outcome: number } | null;

const LABEL = "pl-2 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500";

function Chevron({ open }: { open: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cn("flex-shrink-0 text-zinc-500 transition-transform duration-150 motion-reduce:transition-none", !open && "-rotate-90")}><path d="m6 9 6 6 6-6" /></svg>
  );
}

function GameLines({
  league, family, event, selected, format, drill, compact, bet, onOpen,
}: {
  league: LeagueId;
  family: FamilySummary;
  event: EventHeader;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: GameDrill;
  compact: boolean;
  bet: BetActions;
  onOpen: (rowId: RowId, family: FamilyId, target: CellTarget) => void;
}) {
  const view = useBoard(league, family.id);
  const live = event.live !== undefined;
  const rowId = useMemo(
    () => live ? inplayRowId(event.id) : view.rowIds.find((id) => { const ref = view.rows.get(id); return ref?.eventId === event.id && ref.entity === null; }) ?? "",
    [live, view, event.id],
  );
  const specs = family.markets;
  const row = useRow(league, family.id, rowId, event, specs);
  const order = marketDisplayOrder(specs);
  const minStake = useContext(MinStakeContext);
  const markets: readonly (MarketCells | null)[] = row ? rowCells(row, specs, event, selected, format, minStake) : [];
  const flash = flashCells([], markets);
  const cellBet = useCellBet(bet, { kind: "ladder", league, event: ladderEventOf(event), family: family.id, entity: "" }, markets, matchupLabel(event, league, "spoken"), (target) => onOpen(rowId, family.id, target));
  const rowSelected = drill?.rowId === rowId && drill.family === family.id;
  const lines = rowLines(specs, false);
  const threeWay = lines.includes("draw");
  const marketCols = compact ? `repeat(${order.length}, minmax(0, 1fr))` : `repeat(${order.length}, minmax(120px, 176px))`;
  const cellHeight = compact ? "h-11" : "h-[30px]";
  return (
    <div className={cn("grid gap-2", compact && "w-full")} style={{ gridTemplateColumns: threeWay ? `${compact ? "72px" : "88px"} ${marketCols}` : marketCols }}>
      {threeWay && (
        <div className="flex min-w-0 flex-col gap-1">
          <span className={cn(LABEL, "pl-1")} aria-hidden="true">&nbsp;</span>
          {matchupSides(event, lines, drawName(specs)).map(({ line, name }) => (
            <span key={line} className={cn("flex items-center pl-1 text-[12px] leading-[14px] text-zinc-400", cellHeight)}>
              <span className="line-clamp-2 break-words">{name}</span>
            </span>
          ))}
        </div>
      )}
      {order.map((slot) => {
        const market = markets[slot] ?? null;
        return (
          <div key={slot} className="flex min-w-0 flex-col gap-1">
            <span className={cn(LABEL, "pl-1")}>{specs[slot].label}</span>
            {market === null
              ? lines.map((line) => <div key={line} className={cn("rounded-md bg-white/[0.02]", cellHeight)} />)
              : lineSides(market, lines).map((index, row) => index === null ? (
                <div key={lines[row]} className={cellHeight} aria-hidden="true" />
              ) : (
                <PriceCell
                  key={lines[row]}
                  side={market.sides[index]}
                  flash={flash[slot]?.[index] ?? null}
                  selected={rowSelected && drill?.slot === slot && drill.outcome === market.sides[index].outcome}
                  compact={compact}
                  onOpen={() => onOpen(rowId, family.id, { slot, outcome: market.sides[index].outcome, main: market.main })}
                  bet={cellBet}
                  target={{ slot, outcome: market.sides[index].outcome, main: market.main }}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}

const MarketRows = memo(function MarketRows({
  league, family, event, selected, format, drill, compact, dismissed, bet, onOpen, onHide, onManageHidden,
}: {
  league: LeagueId;
  family: FamilySummary;
  event: EventHeader;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: GameDrill;
  compact: boolean;
  dismissed: (rowId: RowId, family: FamilyId) => boolean;
  bet: BetActions;
  onOpen: (rowId: RowId, family: FamilyId, target: CellTarget) => void;
  onHide: (row: HiddenRow, family: FamilyId) => void;
  onManageHidden: () => void;
}) {
  const listed = useEventRowIds(league, family.id, event, family.markets);
  const cols = subjectCols(family, compact);
  const rowIds = useMemo(() => listed.rowIds.filter((rowId) => !dismissed(rowId, family.id)), [listed, family.id, dismissed]);
  const hidden = listed.rowIds.length - rowIds.length;
  if (listed.loading) {
    return (
      <div className="flex flex-col gap-1.5 px-4 py-2" aria-busy="true" aria-label={`Loading ${family.label}`}>
        {[0, 1, 2].map((index) => <div key={index} className={cn("animate-pulse rounded-md bg-white/[0.03] motion-reduce:animate-none", compact ? "h-[50px]" : "h-[38px]")} />)}
      </div>
    );
  }
  if (listed.rowIds.length === 0) {
    return <div className="px-4 py-3 text-[12px] text-zinc-500">Books have not posted {family.label} for this game yet.</div>;
  }
  return (
    <>
      {rowIds.map((rowId) => (
        <LiveSubjectRow
          key={rowId}
          league={league}
          family={family}
          rowId={rowId}
          event={event}
          selected={selected}
          format={format}
          gridCols={cols}
          drillOutcome={drill?.rowId === rowId && drill.family === family.id ? drill.outcome : null}
          rowSelected={drill?.rowId === rowId && drill.family === family.id}
          compact={compact}
          bet={bet}
          onOpen={(id, target) => onOpen(id, family.id, target)}
          onHide={(row) => onHide(row, family.id)}
        />
      ))}
      <HiddenCountButton count={hidden} noun="row" onClick={onManageHidden} className={cn("my-1", compact ? "ml-1" : "ml-2")} />
    </>
  );
});

export function GameView({
  league, event, linesFamily, categories, category, open, selected, format, drill, compact = false, dismissed, bet, onCategory, onToggle, onOpen, onHide, onManageHidden,
}: {
  league: LeagueId;
  event: EventHeader;
  linesFamily: FamilySummary | undefined;
  categories: readonly FamilyCategory[];
  category: string | null;
  open: readonly FamilyId[];
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: GameDrill;
  compact?: boolean;
  dismissed: (rowId: RowId, family: FamilyId) => boolean;
  bet: BetActions;
  onCategory: (label: string | null) => void;
  onToggle: (family: FamilyId) => void;
  onOpen: (rowId: RowId, family: FamilyId, target: CellTarget) => void;
  onHide: (row: HiddenRow, family: FamilyId) => void;
  onManageHidden: () => void;
}) {
  const shown = category === null ? categories : categories.filter((entry) => entry.label === category);
  return (
    <div className="scrollbar-custom scrollbar-color-primary min-h-0 flex-grow overflow-y-auto">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3 border-b border-surface-line px-4 py-3">
        <div className="flex min-w-0 flex-grow items-center gap-2.5">
          <MatchupLogos event={event} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 className="truncate text-[16px] font-bold leading-[22px] text-foreground" style={{ fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif" }}>{matchupLabel(event, league, "full")}</h2>
            {event.live
              ? <><LiveState live={event.live} className="self-start" /><span className="data-num text-[12px] text-zinc-500">Started {startedAt(event.startMs)}</span></>
              : <span className="data-num text-[12px] text-zinc-500">{metaDate(event.startMs)} · <span className={KICKOFF_TIME}>{clock(event.startMs)}</span></span>}
          </div>
          <StarButton league={league} event={event} className="h-8 w-8" />
        </div>
        {linesFamily && !isTournament(event) && <GameLines league={league} family={linesFamily} event={event} selected={selected} format={format} drill={drill} compact={compact} bet={bet} onOpen={onOpen} />}
      </div>
      <div role="tablist" aria-label="Prop category" className="sticky top-0 z-[3] flex h-10 items-center gap-0.5 overflow-x-auto border-b border-surface-line bg-surface-raised px-2 [scrollbar-width:none]">
        {[null, ...categories.map((entry) => entry.label)].map((label) => (
          <button
            key={label ?? "all"}
            type="button"
            role="tab"
            aria-selected={category === label}
            onClick={() => onCategory(label)}
            className={cn(
              "flex h-10 flex-shrink-0 items-center px-2.5 text-[13px] font-medium whitespace-nowrap transition-colors",
              category === label ? "font-semibold text-foreground shadow-[inset_0_-2px_0_#00d4ff]" : "text-zinc-400 shadow-[inset_0_-2px_0_transparent] hover:text-foreground",
            )}
          >
            {label ?? "All"}
          </button>
        ))}
      </div>
      {shown.map((entry) => (
        <div key={entry.label}>
          {category === null && (
            <h3 className={cn("sticky top-10 z-[2] flex h-9 items-center border-b border-surface-line bg-surface-chrome text-[13px] font-bold text-foreground", compact ? "px-3" : "px-4")} style={{ fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif" }}>{entry.label}</h3>
          )}
          {entry.groups.map((group) => (
            <section key={group.label} aria-label={group.label}>
              {group.families.some((family) => family.kind === "lines" && familyTitle(family) !== family.label) && (
                <h4 className={cn("flex h-8 items-center text-[12px] font-semibold text-zinc-300", compact ? "px-3" : "px-4")}>{group.label}</h4>
              )}
              {group.families.map((family) => {
                const isOpen = open.includes(family.id);
                const spec = family.markets[0];
                const order = outcomeDisplayOrder(spec);
                const cols = subjectCols(family, compact);
                return (
                  <div key={family.id}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => onToggle(family.id)}
                      className={cn("grid w-full scroll-mt-[76px] items-center justify-start gap-x-2 border-t border-b border-t-white/[0.12] border-b-surface-line bg-surface-overlay text-left hover:bg-white/[0.06]", compact ? "h-11 px-3" : "h-9 px-4")}
                      style={{ gridTemplateColumns: isOpen ? cols : "1fr" }}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Chevron open={isOpen} />
                        <span className="truncate text-[12.5px] font-semibold text-foreground">{family.label}</span>
                      </span>
                      {isOpen && order.map((outcome) => (
                        <span key={outcome} className={cn(LABEL, compact && "pl-0 text-center")}>{outcomeName(spec, event, outcome)}</span>
                      ))}
                    </button>
                    {isOpen && (
                      <MarketRows league={league} family={family} event={event} selected={selected} format={format} drill={drill} compact={compact} dismissed={dismissed} bet={bet} onOpen={onOpen} onHide={onHide} onManageHidden={onManageHidden} />
                    )}
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      ))}
      <div className="h-6" />
    </div>
  );
}
